import React, { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Languages, Calendar, Clock, User } from "lucide-react";
import axios from "axios";
import SEO from "@/components/SEO";
import { buildKeywords, buildDescription } from "@/lib/seoKeywords";
import { localizeError } from "@/lib/errors";

const LOGO = "https://customer-assets.emergentagent.com/job_beauty-hub-manage/artifacts/h3pmooxo_image.png";

export default function PublicBooking() {
  const { t, lang, toggleLang } = useLanguage();
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selection, setSelection] = useState(null); // { kind, id, name, price }
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    (async () => {
      const [s, p] = await Promise.all([
        axios.get(`${API_BASE}/public/services`),
        axios.get(`${API_BASE}/public/packages`),
      ]);
      setServices(s.data); setPackages(p.data);
    })();
  }, []);

  useEffect(() => {
    if (!date) return;
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/public/slots?date=${date}`);
        setSlots(r.data.slots);
      } catch (err) {
        console.error("[PublicBooking] failed to load slots:", err);
      }
    })();
  }, [date]);

  const book = async () => {
    if (!selection || !date || !slot || !name || !phone) { toast.error(t("required_field")); return; }
    try {
      const payload = {
        customer_name: name, customer_phone: phone,
        date, time_slot: slot, language: lang,
      };
      if (selection.kind === "service") payload.service_id = selection.id;
      else payload.package_id = selection.id;
      const r = await axios.post(`${API_BASE}/public/book`, payload);
      setDone(r.data);
      toast.success(t("book_success"));
    } catch (e) {
      toast.error(localizeError(e, t, lang));
    }
  };

  return (
    <div className="min-h-screen" data-testid="public-booking-page">
      <SEO
        title={lang === "ar" ? "حجز موعد صالون اونلاين | iSaloon" : "Online Salon Appointment Booking | iSaloon"}
        description={buildDescription({
          lang,
          salonName: lang === "ar" ? "آي صالون" : "iSaloon",
          city: lang === "ar" ? "الدوحة" : "Doha",
          services,
          topKeywords: buildKeywords({ lang, services, city: lang === "ar" ? "الدوحة" : "Doha" }).top,
        })}
        canonical={typeof window !== "undefined" ? `${window.location.origin}/book` : ""}
        keywords={buildKeywords({ lang, services, city: lang === "ar" ? "الدوحة" : "Doha" }).csv}
        type="website"
        lang={lang}
        siteName="iSaloon"
        alternates={typeof window !== "undefined" ? { ar: `${window.location.origin}/book?lang=ar`, en: `${window.location.origin}/book?lang=en` } : {}}
      />
      {/* Hero */}
      <div className="relative h-64 md:h-80 overflow-hidden"
        style={{
          background:
            "radial-gradient(circle at 15% 20%, rgba(201,134,139,0.30) 0%, transparent 50%), radial-gradient(circle at 85% 80%, rgba(184,137,61,0.22) 0%, transparent 55%), linear-gradient(180deg, #FDF1EC 0%, #F5E3D9 100%)",
        }}>
        <div className="relative z-10 h-full flex items-center justify-between px-6 md:px-12">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="iSaloon" className="w-14 h-14 md:w-20 md:h-20 object-contain"/>
            <div>
              <div className="gold-text font-heading text-3xl md:text-5xl leading-none">{t("hero_title")}</div>
              <div className="text-[var(--text)] text-sm md:text-base mt-2">{t("hero_subtitle")}</div>
            </div>
          </div>
          <button onClick={toggleLang} data-testid="public-lang-toggle" className="flex items-center gap-2 px-3 py-2 rounded-md border border-[var(--border)] text-[var(--text)] bg-white/80 backdrop-blur">
            <Languages size={16}/><span>{lang === "ar" ? "EN" : "ع"}</span>
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-8">
        {done ? (
          <div className="card-surface p-8 text-center max-w-lg mx-auto">
            <div className="gold-text font-heading text-4xl mb-2">✓ {t("book_success")}</div>
            <div className="text-[var(--text)] space-y-1 mt-4">
              <div><strong>{lang === "ar" ? done.item_name_ar : done.item_name_en}</strong></div>
              <div>{done.date} · {done.time_slot}</div>
              <div>{done.customer_name} · <span dir="ltr">{done.customer_phone}</span></div>
            </div>
            <a href={`/card/${done.customer_id}`} className="btn-gold inline-block mt-6 px-6 py-3 rounded-md">{t("view_card")}</a>
          </div>
        ) : (
          <>
            <div>
              <h2 className="font-heading text-2xl md:text-3xl text-[var(--text)] mb-4">{t("services_available")}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {services.map(s => (
                  <button key={s.service_id} onClick={() => setSelection({ kind: "service", id: s.service_id, name: lang==="ar"?s.name_ar:s.name_en, price: s.price })}
                    data-testid={`public-service-${s.service_id}`}
                    className={`card-surface p-4 text-start transition ${selection?.id === s.service_id ? "border-[var(--gold)]" : "hover:border-[var(--gold)]"}`}>
                    <div className="text-sm text-[var(--text)] min-h-[2.5rem]">{lang === "ar" ? s.name_ar : s.name_en}</div>
                    <div className="flex items-end justify-between mt-3">
                      <div className="gold-text font-heading text-2xl">{s.price}</div>
                      <div className="text-xs text-[var(--text-muted)]">{s.duration_min} {t("duration").split(' ')[0]}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {packages.length > 0 && (
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-[var(--text)] mb-4">{t("packages_available")}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {packages.map(p => (
                    <button key={p.package_id} onClick={() => setSelection({ kind: "package", id: p.package_id, name: lang==="ar"?p.name_ar:p.name_en, price: p.price })}
                      data-testid={`public-package-${p.package_id}`}
                      className={`card-surface p-5 text-start transition ${selection?.id === p.package_id ? "border-[var(--rose)]" : "hover:border-[var(--rose)]"}`}>
                      <div className="font-heading text-xl text-[var(--text)]">{lang === "ar" ? p.name_ar : p.name_en}</div>
                      <div className="text-sm text-[var(--text-muted)] mt-2">{lang === "ar" ? p.description_ar : p.description_en}</div>
                      <div className="gold-text font-heading text-2xl mt-4">{p.price}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="card-surface p-5 md:p-8 space-y-4">
              <h2 className="font-heading text-2xl text-[var(--text)]">{t("book_now")}</h2>
              {selection ? (
                <div className="flex items-center gap-3 text-sm">
                  <span className="badge badge-gold">{selection.name}</span>
                  <span className="gold-text">{selection.price}</span>
                </div>
              ) : <div className="text-sm text-[var(--text-muted)]">{t("choose_service_or_package")}</div>}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className="text-xs text-[var(--text-muted)] flex items-center gap-1"><User size={12}/>{t("your_name")}</label><input className="input-dark" value={name} onChange={e => setName(e.target.value)} data-testid="public-name"/></div>
                <div><label className="text-xs text-[var(--text-muted)]">{t("your_phone")}</label><input className="input-dark" value={phone} onChange={e => setPhone(e.target.value)} data-testid="public-phone"/></div>
                <div><label className="text-xs text-[var(--text-muted)] flex items-center gap-1"><Calendar size={12}/>{t("select_date")}</label><input type="date" className="input-dark" value={date} onChange={e => setDate(e.target.value)} data-testid="public-date"/></div>
                <div className="md:col-span-2">
                  <label className="text-xs text-[var(--text-muted)] flex items-center gap-1"><Clock size={12}/>{t("select_time")}</label>
                  <div className="grid grid-cols-4 md:grid-cols-6 gap-2 mt-1">
                    {slots.map(s => (
                      <button key={s.time_slot} type="button" disabled={s.remaining <= 0} onClick={() => setSlot(s.time_slot)}
                        data-testid={`public-slot-${s.time_slot}`}
                        className={`p-2 rounded text-xs border transition ${slot === s.time_slot ? "btn-gold" : "border-[var(--border)] hover:border-[var(--gold)] text-[var(--text)]"} ${s.remaining <= 0 ? "opacity-40 cursor-not-allowed" : ""}`}>
                        <div>{s.time_slot}</div>
                        <div className="text-[10px] opacity-70">{s.remaining <= 0 ? t("full") : `${s.remaining}/${s.capacity}`}</div>
                      </button>
                    ))}
                    {slots.length === 0 && <div className="col-span-full text-xs text-[var(--text-muted)] py-2">{t("select_date")}</div>}
                  </div>
                </div>
              </div>

              <button onClick={book} disabled={!selection} data-testid="public-book-btn" className="btn-gold w-full py-3 rounded-md font-medium">{t("book")}</button>
            </div>
          </>
        )}
      </div>

      <footer className="text-center text-xs text-[var(--text-disabled)] py-6">{t("powered_by")} · {t("developed_by")}</footer>
    </div>
  );
}
